import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Space,
  Popconfirm,
  message,
  Tag,
  Card,
  Row,
  Col,
  Tooltip,
  Avatar,
  Typography,
  Divider,
  Upload,
  UploadProps,
  Select,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { RcFile } from "rc-upload/lib/interface";
import { Dayjs } from "dayjs";
import axios, { AxiosProgressEvent } from "axios";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  BookOutlined,
  CalendarOutlined,
  UserOutlined,
  StarOutlined,
  StarFilled,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import JoditEditor from "jodit-react";

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

// Type Definitions
interface FAQ {
  question: string;
  answer: string;
}

interface AuthorSocialMedia {
  platform: string;
  url: string;
}

interface Article {
  _id: string;
  title: string;
  author?: string;
  description: string;
  content?: string;
  image?: string;
  badge?: string;
  date?: string;
  publishedDate?: string;
  slug?: string;
  canonical?: string;
  researchAssociate?: string;
  altTag?: string;
  companyName?: string;
  designation?: string;
  location?: string;
  likes?: number;
  commentCount?: number;
  saveCount?: number;
  expertEdage?: boolean;
  meta?: string;
  faq?: FAQ[];
  authorProfile?: string;
  authorSocialMedia?: AuthorSocialMedia[];
  // Backend uses `published`; keep legacy `isPublished` as fallback
  published?: boolean;
  isPublished?: boolean;
  userPostDataInfo?: Array<{
    name: string;
    role: string;
    emailId: string;
    createdAt: string;
    updatedAt: string;
  }>;
}

interface ArticleFormValues {
  title: string;
  author?: string;
  description: string;
  content?: string;
  image?: string;
  badge?: string;
  date?: Dayjs;
  publishedDate?: Dayjs;
  slug?: string;
  canonical?: string;
  researchAssociate?: string;
  altTag?: string;
  companyName?: string;
  designation?: string;
  location?: string;
  likes?: number;
  commentCount?: number;
  saveCount?: number;
  expertEdage?: boolean;
  meta?: string;
  faq?: FAQ[];
  authorProfile?: string;
  authorSocialMedia?: AuthorSocialMedia[];
}

interface ApiResponse {
  blogs: Article[];
  total: number;
}

interface UploadResponse {
  filename: string;
  message?: string;
}

// API Configuration
const API_BASE_URL: string = "https://service.ireedindia.com/v1";

const SOCIAL_PLATFORMS = [
  "Facebook",
  "Twitter",
  "LinkedIn",
  "Instagram",
  "YouTube",
  "Pinterest",
  "Threads",
  "TikTok",
  "Website",
  "Other",
];

const ArticleAdminPanel: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [totalArticles, setTotalArticles] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState<boolean>(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [viewingArticle, setViewingArticle] = useState<Article | null>(null);
  const [form] = Form.useForm<ArticleFormValues>();
  const [searchText, setSearchText] = useState<string>("");
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [publishFilter, setPublishFilter] = useState<'all' | 'published' | 'unpublished'>('all');
  const [fileName, setFileName] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Author profile upload state
  const [authorProfileFileName, setAuthorProfileFileName] = useState<string>("");
  const [authorProfileUploading, setAuthorProfileUploading] = useState<boolean>(false);

  // Get user role from localStorage
  const getUserRole = (): string => {
    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    return userDetails?.role || '';
  };
  const userRole = getUserRole();

  const descriptionEditor = useRef<any>(null);
  const contentEditor = useRef<any>(null);
  const [description, setDescription] = useState<string>("");
  const [content, setContent] = useState<string>("");

  const joditConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: "Start typing...",
      toolbarAdaptive: false,
      toolbarSticky: false,
      uploader: { insertImageAsBase64URI: true },
      disablePlugins: "drag-and-drop-element",
      controls: {
        image: {
          exec: function (editor: any) {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*";
            input.onchange = function () {
              const file = input.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = function (e: ProgressEvent<FileReader>) {
                  if (e.target?.result) {
                    editor.selection.insertImage(e.target.result as string);
                  }
                };
                reader.readAsDataURL(file);
              }
            };
            input.click();
          },
          icon: "image",
          tooltip: "Insert Image",
        },
      },
      style: { textAlign: "left" },
      height: 300,
    }),
    []
  );

  const descriptionConfig = useMemo(() => ({ ...joditConfig, placeholder: "Enter article description...", height: 250 }), [joditConfig]);
  const contentConfig = useMemo(() => ({ ...joditConfig, placeholder: "Enter article content... (Optional)", height: 300 }), [joditConfig]);

  useEffect(() => {
    const errorHandler = (e: ErrorEvent) => {
      if (e.message.includes("ResizeObserver loop limit exceeded")) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", errorHandler);
    return () => window.removeEventListener("error", errorHandler);
  }, []);

  // ─── Upload Handlers ──────────────────────────────────────────────────────────

  const customUploadRequest: UploadProps["customRequest"] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;
    const formData = new FormData();
    formData.append("file", file as RcFile);
    try {
      const uploadUrl = `${API_BASE_URL.replace("/v1", "")}/upload`;
      const response = await axios.post<UploadResponse>(uploadUrl, formData, {
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress?.({ percent });
          }
          
        },
      });
      onSuccess?.(response.data);
    } catch (error: any) {
      message.error("Upload failed");
      onError?.(error);
    }
  };

  const authorProfileUploadRequest: UploadProps["customRequest"] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;
    const formData = new FormData();
    formData.append("file", file as RcFile);
    try {
      const uploadUrl = `${API_BASE_URL.replace("/v1", "")}/upload`;
      const response = await axios.post<UploadResponse>(uploadUrl, formData, {
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress?.({ percent });
          }
        },
      });
      onSuccess?.(response.data);
    } catch (error: any) {
      message.error("Upload failed");
      onError?.(error);
    }
  };

  const handleFileUpload: UploadProps["onChange"] = (info) => {
    if (info.file.status === "uploading") { setUploading(true); return; }
    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
      if (info.file.response?.filename) {
        setFileName(info.file.response.filename);
        form.setFieldsValue({ image: info.file.response.filename });
      } else {
        message.error("Upload succeeded but no filename received");
      }
      setUploading(false);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
      setUploading(false);
    }
  };

  const handleAuthorProfileUpload: UploadProps["onChange"] = (info) => {
    if (info.file.status === "uploading") { setAuthorProfileUploading(true); return; }
    if (info.file.status === "done") {
      message.success(`${info.file.name} uploaded successfully`);
      if (info.file.response?.filename) {
        setAuthorProfileFileName(info.file.response.filename);
        form.setFieldsValue({ authorProfile: info.file.response.filename });
      } else {
        message.error("Upload succeeded but no filename received");
      }
      setAuthorProfileUploading(false);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} upload failed.`);
      setAuthorProfileUploading(false);
    }
  };

  const beforeUploadValidation: UploadProps["beforeUpload"] = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) message.error("You can only upload image files!");
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) message.error("Image must be smaller than 5MB!");
    return isImage && isLt5M;
  };

  // ─── Data Fetching ────────────────────────────────────────────────────────────

  const fetchArticles = async (): Promise<void> => {
    setLoading(true);
    try {
      const userDetailsString = localStorage.getItem('userDetails');
      const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
      const role = userDetails?.role;
      const userId = userDetails?.userId;

      let url = `${API_BASE_URL}/articles?pageNumber=0&size=1000`;
      if (role !== 'admin' && userId) {
        url += `&userId=${encodeURIComponent(userId)}`;
      }

      const response = await axios.get<ApiResponse>(url);
      const { blogs = [], total = 0 } = response.data;
      setArticles(blogs);
      setFilteredArticles(blogs);
      setTotalArticles(total);
    } catch (error) {
      message.error("Failed to fetch articles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArticles(); }, []);

  useEffect(() => {
    let filtered = articles.filter((article) =>
      ["title", "description", "author"].some(field =>
        (article[field as keyof Article] as string)?.toLowerCase().includes(searchText.toLowerCase())
      )
    );
    if (userRole === 'admin' && publishFilter !== 'all') {
      filtered = filtered.filter(article => {
        const isPub = article.published ?? article.isPublished ?? false;
        return publishFilter === 'published' ? isPub : !isPub;
      });
    }
    setFilteredArticles(filtered);
  }, [searchText, articles, publishFilter, userRole]);

  // ─── Form Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async (values: ArticleFormValues): Promise<void> => {
    setLoading(true);
    try {
      const userDetailsString = localStorage.getItem('userDetails');
      const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
      const userId = userDetails?.userId;

      const formData = {
        ...values,
        description: description || values.description,
        content: content || values.content,
        // Use format to prevent timezone shifting (e.g., IST 00:00 becomes UTC 18:30 of previous day)
        date: values.date ? values.date.format('YYYY-MM-DD') + 'T00:00:00.000Z' : new Date().toISOString(),
        publishedDate: values.publishedDate ? values.publishedDate.format('YYYY-MM-DD') + 'T00:00:00.000Z' : undefined,
        faq: values.faq?.filter((item) => item.question && item.answer) || [],
        authorSocialMedia: values.authorSocialMedia?.filter((item) => item.platform && item.url) || [],
        userId: userId,
      };
      const url = editingArticle ? `${API_BASE_URL}/articles/${editingArticle._id}` : `${API_BASE_URL}/articles`;
      const method = editingArticle ? "patch" : "post";
      await axios({ method, url, data: formData });
      message.success(`Article ${editingArticle ? "updated" : "created"} successfully`);
      setIsModalVisible(false);
      resetForm();
      await fetchArticles();
    } catch (error) {
      message.error(`Failed to ${editingArticle ? "update" : "create"} article`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setEditingArticle(null);
    setFileName("");
    setAuthorProfileFileName("");
    setDescription("");
    setContent("");
  };

  // ─── CRUD Actions ─────────────────────────────────────────────────────────────

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/articles/${id}`);
      message.success("Article deleted successfully");
      await fetchArticles();
    } catch (error) {
      message.error("Failed to delete article");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpertEdage = async (article: Article) => {
    if (!article._id) { message.error("Invalid article ID"); return; }
    setTogglingId(article._id);
    try {
      const newStatus = !article.expertEdage;
      await axios.patch(`${API_BASE_URL}/articles/${article._id}`, { expertEdage: newStatus });
      message.success(`Article updated successfully`);
      await fetchArticles();
    } catch (error) {
      message.error("Failed to update Expert Edage status");
    } finally {
      setTogglingId(null);
    }
  };

  const handlePublish = async (id: string, currentStatus: boolean) => {
    setLoading(true);
    try {
      await axios.patch(`${API_BASE_URL}/articles/${id}`, { published: !currentStatus });
      message.success(`Article ${!currentStatus ? 'published' : 'unpublished'} successfully`);
      await fetchArticles();
    } catch (error) {
      message.error('Error updating publish status');
    } finally {
      setLoading(false);
    }
  };

  // ─── Modal Openers ────────────────────────────────────────────────────────────

  const openCreateModal = () => {
    resetForm();
    form.setFieldsValue({
      date: dayjs(),
      publishedDate: dayjs(),
    });
    setIsModalVisible(true);
  };

  const openEditModal = (article: Article) => {
    setEditingArticle(article);
    setFileName(article.image || "");
    setAuthorProfileFileName(article.authorProfile || "");
    setDescription(article.description || "");
    setContent(article.content || "");
    form.setFieldsValue({
      ...article,
      date: article.date ? dayjs(article.date) : dayjs(),
      publishedDate: article.publishedDate ? dayjs(article.publishedDate) : undefined,
      faq: article.faq || [{ question: "", answer: "" }],
      authorSocialMedia: article.authorSocialMedia || [{ platform: "", url: "" }],
    });
    setIsModalVisible(true);
  };

  const openViewModal = (article: Article) => { setViewingArticle(article); setIsViewModalVisible(true); };

  // ─── Table Columns ────────────────────────────────────────────────────────────

  const columns: ColumnsType<Article> = [
    {
      title: "Image", dataIndex: "image", key: "image", width: 80,
      render: (image) => <Avatar shape="square" size={50} src={image ? `${API_BASE_URL.replace('/v1', '')}/image/${image}` : undefined} icon={<BookOutlined />} />,
    },
    { title: "Title", dataIndex: "title", key: "title", width: 200, ellipsis: true, render: (text) => <Text strong>{text}</Text> },
    { title: "Author", dataIndex: "author", key: "author", width: 250, render: (author) => <><UserOutlined className="mr-2" />{author || "N/A"}</> },
    { title: "Date", dataIndex: "publishedDate", key: "publishedDate", width: 140, render: (date) => <><CalendarOutlined className="mr-2" />{date ? dayjs(date).format("MMM DD, YYYY") : "N/A"}</> },
    { title: "Badge", dataIndex: "badge", key: "badge", width: 100, render: (badge) => badge ? <Tag color="blue">{badge}</Tag> : "-" },
    {
      title: "Expert Edge", dataIndex: "expertEdage", key: "expertEdage", width: 110, align: 'center',
      render: (isExpert) => isExpert ? <Tag color="purple" icon={<StarOutlined />}>Active</Tag> : "-",
    },
    {
      title: "Status", dataIndex: "published", key: "published", width: 130,
      render: (published: boolean, record: Article) => {
        const isPub = published ?? record.isPublished ?? false;
        return (
          <span style={{
            padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
            backgroundColor: isPub ? '#d1fae5' : '#fef3c7',
            color: isPub ? '#065f46' : '#92400e'
          }}>
            {isPub ? '✓ Published' : 'Draft'}
          </span>
        );
      },
    },
    ...(userRole === 'admin' ? [
      {
        title: "Created By", dataIndex: "userPostDataInfo", key: "createdBy", width: 220,
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          return (
            <div style={{ fontSize: '12px' }}>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>{user.name}</div>
              <div style={{ color: '#6b7280' }}>{user.emailId}</div>
              <div style={{ color: '#1890ff', fontSize: '11px' }}>{user.role}</div>
            </div>
          );
        },
      },
      {
        title: "Created At", dataIndex: "userPostDataInfo", key: "createdAt", width: 120,
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.createdAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
      {
        title: "Updated At", dataIndex: "userPostDataInfo", key: "updatedAt", width: 120,
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.updatedAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
    ] : []),
    {
      title: "Actions", key: "actions", fixed: "right" as const, width: 220,
      render: (_, record) => (
        <Space size="small" wrap>
          {userRole === 'admin' && (
            <Tooltip title={record.expertEdage ? "Remove from Expert Edge" : "Add to Expert Edge"}>
              <Button
                icon={record.expertEdage ? <StarFilled style={{ color: '#fadb14' }} /> : <StarOutlined />}
                size="small"
                onClick={() => handleToggleExpertEdage(record)}
                loading={togglingId === record._id}
                type={record.expertEdage ? "default" : "primary"}
                style={record.expertEdage ? { backgroundColor: '#722ed1', color: '#fff', borderColor: 'transparent' } : {}}
              />
            </Tooltip>
          )}
          <Tooltip title="View">
            <Button type="default" size="small" icon={<EyeOutlined />} onClick={() => openViewModal(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          {userRole === 'admin' && (
            <Tooltip title={(record.published ?? record.isPublished) ? 'Unpublish' : 'Publish'}>
              <Button
                type={(record.published ?? record.isPublished) ? "default" : "primary"}
                size="small"
                onClick={() => handlePublish(record._id, (record.published ?? record.isPublished) || false)}
                style={{ backgroundColor: (record.published ?? record.isPublished) ? '#faad14' : '#52c41a', color: '#fff', borderColor: 'transparent' }}
              >
                {(record.published ?? record.isPublished) ? 'P' : 'U'}
              </Button>
            </Tooltip>
          )}
          {userRole === 'admin' && (
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this article?"
                onConfirm={() => record._id && handleDelete(record._id)}
                okText="Yes"
                cancelText="No"
                placement="left"
              >
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Card className="mb-6 shadow-lg border-0">
          <div className="flex justify-between items-center">
            <div>
              <Title level={2} className="mb-2 text-gray-800"><BookOutlined className="mr-3 text-blue-600" />Articles Management</Title>
              <Text type="secondary" className="text-lg">Manage your articles efficiently</Text>
            </div>
            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={openCreateModal} className="bg-gradient-to-r from-blue-500 to-indigo-600 border-0 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200">
              Add New Article
            </Button>
          </div>
        </Card>

        {/* Search */}
        <Card className="mb-6 shadow-md border-0">
          <Input
            placeholder="Search articles..."
            prefix={<SearchOutlined className="text-gray-400" />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="large"
            className="rounded-lg"
          />
        </Card>

        {/* Stats */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} md={8}>
            <Card className="text-center shadow-md border-0 bg-gradient-to-r from-blue-400 to-blue-600 text-white">
              <div className="text-3xl font-bold">{totalArticles}</div>
              <div className="text-blue-100">Total Articles</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card className="text-center shadow-md border-0 bg-gradient-to-r from-orange-400 to-orange-600 text-white">
              <div className="text-3xl font-bold">{articles.filter((a) => a.published || a.isPublished).length}</div>
              <div className="text-orange-100">Published</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card className="text-center shadow-md border-0 bg-gradient-to-r from-purple-400 to-purple-600 text-white">
              <div className="text-3xl font-bold">{articles.filter((a) => a.expertEdage).length}</div>
              <div className="text-purple-100">Expert Edge</div>
            </Card>
          </Col>
        </Row>

        {/* Table */}
        <Card className="shadow-lg border-0">
          {userRole === 'admin' && (
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontWeight: 500, fontSize: 15 }}>Filter:</span>
              <select
                value={publishFilter}
                onChange={e => setPublishFilter(e.target.value as 'all' | 'published' | 'unpublished')}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 15 }}
              >
                <option value="all">All</option>
                <option value="published">Published</option>
                <option value="unpublished">Unpublished</option>
              </select>
            </div>
          )}
          <Table
            columns={columns}
            dataSource={filteredArticles}
            rowKey="_id"
            loading={loading}
            scroll={{ x: 1600 }}
            pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true, showTotal: (total) => `Total ${total} articles` }}
            className="rounded-lg"
          />
        </Card>

        {/* ── Create / Edit Modal ─────────────────────────────────────────────── */}
        <Modal
          title={editingArticle ? "Edit Article" : "Create Article"}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={1200}
          className="top-4"
        >
          <div className="max-h-[85vh] overflow-y-auto p-2">
            <Form form={form} layout="vertical" onFinish={handleSubmit}>

              {/* Basic Fields */}
              <Form.Item label="Title" name="title" rules={[{ required: true }]}><Input size="large" /></Form.Item>
              <Row gutter={16}>
                <Col span={12}><Form.Item label="Author" name="author" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col span={12}><Form.Item label="Badge" name="badge" rules={[{ required: true }]}><Input /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item label="Date" name="date" rules={[{ required: true }]}><DatePicker className="w-full" /></Form.Item></Col>
                <Col span={12}><Form.Item label="Published Date" name="publishedDate" rules={[{ required: true }]}><DatePicker className="w-full" /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item label="Research Associate" name="researchAssociate" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col span={12}><Form.Item label="Alt Tag" name="altTag" rules={[{ required: true }]}><Input /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item label="Slug" name="slug" rules={[{ required: true }]}><Input placeholder="Enter article slug (URL-friendly)" /></Form.Item></Col>
                <Col span={12}><Form.Item label="Canonical URL" name="canonical" rules={[{ required: true }]}><Input placeholder="Enter canonical URL" /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}><Form.Item label="Company Name" name="companyName"><Input placeholder="Enter company name" /></Form.Item></Col>
                <Col span={8}><Form.Item label="Designation" name="designation"><Input placeholder="Enter designation" /></Form.Item></Col>
                <Col span={8}><Form.Item label="Location" name="location"><Input placeholder="Enter location" /></Form.Item></Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}><Form.Item label="Meta Description" name="meta" rules={[{ required: true }]}><TextArea rows={2} /></Form.Item></Col>
                <Col span={12}></Col>
              </Row>

              {/* Featured Image */}
              <Form.Item label="Featured Image" name="image" rules={[{ required: true }]}>
                <Upload listType="picture" maxCount={1} customRequest={customUploadRequest} onChange={handleFileUpload} beforeUpload={beforeUploadValidation} accept="image/*">
                  <Button icon={<UploadOutlined />} loading={uploading}>Upload Image</Button>
                </Upload>
                {fileName && <Text type="success" className="block mt-2">✅ Uploaded: {fileName}</Text>}
              </Form.Item>

              {/* ── Author Profile Image ─────────────────────────────────────── */}
              <Form.Item label="Author Profile Image" name="authorProfile">
                <Upload
                  listType="picture"
                  maxCount={1}
                  customRequest={authorProfileUploadRequest}
                  onChange={handleAuthorProfileUpload}
                  beforeUpload={beforeUploadValidation}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />} loading={authorProfileUploading}>
                    Choose Author Profile Image
                  </Button>
                </Upload>
                {authorProfileFileName && (
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar
                      size={48}
                      src={`${API_BASE_URL.replace('/v1', '')}/image/${authorProfileFileName}`}
                      icon={<UserOutlined />}
                    />
                    <Text type="success">✅ Uploaded: {authorProfileFileName}</Text>
                  </div>
                )}
              </Form.Item>

              {/* ── Author Social Media ──────────────────────────────────────── */}
              <Divider>Author Social Media</Divider>
              <Form.List name="authorSocialMedia">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} className="border p-4 rounded mb-4 bg-gray-50">
                        <Row gutter={12} align="middle">
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, "platform"]}
                              label="Platform"
                              rules={[{ message: "Please select a platform" }]}
                            >
                              <Select placeholder="Select platform">
                                {SOCIAL_PLATFORMS.map((p) => (
                                  <Option key={p} value={p}>{p}</Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={14}>
                            <Form.Item
                              {...restField}
                              name={[name, "url"]}
                              label="URL"
                              rules={[
                                { message: "Please enter the URL" },
                                { type: "url", message: "Please enter a valid URL" },
                              ]}
                            >
                              <Input placeholder="https://example.com/profile" />
                            </Form.Item>
                          </Col>
                          <Col span={2} style={{ paddingTop: 4 }}>
                            <Button type="link" danger onClick={() => remove(name)} style={{ padding: 0 }}>
                              Remove
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    ))}
                    <Button type="dashed" onClick={() => add({ platform: "", url: "" })} block icon={<PlusOutlined />}>
                      Add Social Media Link
                    </Button>
                  </>
                )}
              </Form.List>
              <br />
              <br />
              <br />
              {/* FAQ Section */}
              <Divider>FAQ Section</Divider>
              <Form.List name="faq">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} className="border p-4 rounded mb-4 bg-gray-50">
                        <Form.Item {...restField} name={[name, "question"]} label="Question"><Input /></Form.Item>
                        <Form.Item {...restField} name={[name, "answer"]} label="Answer"><TextArea rows={2} /></Form.Item>
                        <Button type="link" danger onClick={() => remove(name)}>Remove FAQ</Button>
                      </div>
                    ))}
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add FAQ</Button>
                  </>
                )}
              </Form.List>

              {/* Rich Text Editors */}
              <Form.Item label="Description (Rich Text)" name="description" rules={[{ required: true }]}>
                <JoditEditor ref={descriptionEditor} value={description} config={descriptionConfig} onBlur={newContent => { setDescription(newContent); form.setFieldsValue({ description: newContent }); }} />
              </Form.Item>
              <Form.Item label="Content (Rich Text)" name="content">
                <JoditEditor ref={contentEditor} value={content} config={contentConfig} onBlur={newContent => { setContent(newContent); form.setFieldsValue({ content: newContent }); }} />
              </Form.Item>

              {/* Submit */}
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={loading} className="bg-blue-600">
                  {editingArticle ? "Update Article" : "Create Article"}
                </Button>
              </div>
            </Form>
          </div>
        </Modal>

        {/* ── View Modal ──────────────────────────────────────────────────────── */}
        <Modal
          title="Article Details"
          open={isViewModalVisible}
          onCancel={() => setIsViewModalVisible(false)}
          footer={<Button onClick={() => setIsViewModalVisible(false)}>Close</Button>}
          width={800}
        >
          {viewingArticle && (
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <Avatar shape="square" size={80} src={viewingArticle.image ? `${API_BASE_URL.replace('/v1', '')}/image/${viewingArticle.image}` : undefined} icon={<BookOutlined />} />
                <div className="flex-1">
                  <Title level={4}>{viewingArticle.title}</Title>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-gray-600">
                    <span><UserOutlined /> {viewingArticle.author}</span>
                    <span><CalendarOutlined /> {dayjs(viewingArticle.publishedDate).format("MMM DD, YYYY")}</span>
                    {viewingArticle.badge && <Tag color="blue">{viewingArticle.badge}</Tag>}
                    {viewingArticle.expertEdage && <Tag color="purple">Expert Edge</Tag>}
                  </div>

                  {/* Author Profile Preview */}
                  {viewingArticle.authorProfile && (
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar
                        size={44}
                        src={`${API_BASE_URL.replace('/v1', '')}/image/${viewingArticle.authorProfile}`}
                        icon={<UserOutlined />}
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>Author Profile Photo</Text>
                    </div>
                  )}

                  {/* Author Social Media Links */}
                  {viewingArticle.authorSocialMedia && viewingArticle.authorSocialMedia.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {viewingArticle.authorSocialMedia.map((sm, idx) => (
                        <a key={idx} href={sm.url} target="_blank" rel="noopener noreferrer">
                          <Tag color="geekblue" style={{ cursor: 'pointer' }}>{sm.platform} ↗</Tag>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <Divider />
              <div><Text strong>Description:</Text><div dangerouslySetInnerHTML={{ __html: viewingArticle.description }} /></div>
              {viewingArticle.content && <div><Text strong>Content:</Text><div dangerouslySetInnerHTML={{ __html: viewingArticle.content }} /></div>}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default ArticleAdminPanel;